// src/components/RecipeFormModal.jsx

import React, { useState, useEffect, useRef } from 'react';
import '../stylings/RecipeFormModal.css';

function RecipeFormModal({ 
  isOpen, 
  onClose, 
  onSave, 
  editMode = false, 
  initialRecipe,
  products = []
}) {
  // State for recipe data
  const [recipe, setRecipe] = useState(
    initialRecipe || {
      id: null,
      slug: '',
      title: '',
      category: 'customer_recipes',
      product_id: '',
      description: '',
      difficulty: 'Easy',
      prep_time_min: '',
      cook_time_min: '',
      servings: '',
      season: 'Year-round',
      image_s3_key: '',
      ingredients: [],
      steps: [],
      tips: [],
      status: 'draft'
    }
  );
  
  // State for ingredient, step, and tip inputs
  const [newIngredient, setNewIngredient] = useState('');
  const [newStep, setNewStep] = useState('');
  const [newTip, setNewTip] = useState('');
  
  // State for image upload
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const fileInputRef = useRef(null);
  
  // Reference for modal content to handle clicks outside
  const modalContentRef = useRef(null);
  
  // Setup effect to initialize modal data when opened
  useEffect(() => {
    if (isOpen && initialRecipe) {
      setRecipe(initialRecipe);
      
      // Set image preview if available
      if (initialRecipe.image_s3_key) {
        setImagePreview(`https://bucks-farmstand-product-images.s3.amazonaws.com/${initialRecipe.image_s3_key}`);
      }
    }
  }, [isOpen, initialRecipe]);
  
  // Handle click outside modal to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalContentRef.current && !modalContentRef.current.contains(event.target)) {
        handleClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);
  
  // Prevent scrolling of background when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  
  // Reset states when modal is closed
  const handleClose = () => {
    setNewIngredient('');
    setNewStep('');
    setNewTip('');
    setImageFile(null);
    setImagePreview('');
    onClose();
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRecipe({
      ...recipe,
      [name]: value
    });
  };
  
  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Clear image selection
  const clearImageSelection = () => {
    setImageFile(null);
    setImagePreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  // Add ingredient to list
  const handleAddIngredient = () => {
    if (newIngredient.trim() !== '') {
      setRecipe({
        ...recipe,
        ingredients: [...recipe.ingredients, newIngredient.trim()]
      });
      setNewIngredient('');
    }
  };
  
  // Remove ingredient from list
  const handleRemoveIngredient = (index) => {
    const updatedIngredients = [...recipe.ingredients];
    updatedIngredients.splice(index, 1);
    setRecipe({
      ...recipe,
      ingredients: updatedIngredients
    });
  };
  
  // Add step to list
  const handleAddStep = () => {
    if (newStep.trim() !== '') {
      setRecipe({
        ...recipe,
        steps: [...recipe.steps, newStep.trim()]
      });
      setNewStep('');
    }
  };
  
  // Remove step from list
  const handleRemoveStep = (index) => {
    const updatedSteps = [...recipe.steps];
    updatedSteps.splice(index, 1);
    setRecipe({
      ...recipe,
      steps: updatedSteps
    });
  };
  
  // Add tip to list
  const handleAddTip = () => {
    if (newTip.trim() !== '') {
      setRecipe({
        ...recipe,
        tips: [...recipe.tips, newTip.trim()]
      });
      setNewTip('');
    }
  };
  
  // Remove tip from list
  const handleRemoveTip = (index) => {
    const updatedTips = [...recipe.tips];
    updatedTips.splice(index, 1);
    setRecipe({
      ...recipe,
      tips: updatedTips
    });
  };
  
  // Generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation
    if (!recipe.title) {
      // In a real implementation, you'd use state for error messages
      alert('Recipe title is required');
      return;
    }
    
    if (recipe.ingredients.length === 0) {
      alert('At least one ingredient is required');
      return;
    }
    
    if (recipe.steps.length === 0) {
      alert('At least one step is required');
      return;
    }
    
    // Validate product_id if selected
    if (recipe.product_id && products.length > 0) {
      const productExists = products.some(p => p.id.toString() === recipe.product_id.toString());
      if (!productExists) {
        alert('Selected product does not exist in inventory');
        return;
      }
    }
    
    // Generate slug if not provided
    let updatedRecipe = { ...recipe };
    if (!updatedRecipe.slug) {
      updatedRecipe.slug = generateSlug(updatedRecipe.title);
    }
    
    // Ensure product_id is null if empty string
    if (updatedRecipe.product_id === '') {
      updatedRecipe.product_id = null;
    }
    
    // Save recipe
    onSave(updatedRecipe, imageFile);
  };
  
  if (!isOpen) return null;

  return (
    <div className="recipe-modal-overlay">
      <div className="recipe-modal-container" ref={modalContentRef}>
        <div className="recipe-modal-header">
          <h2>{editMode ? 'Edit Recipe' : 'Add New Recipe'}</h2>
          <button 
            className="modal-close-btn" 
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        
        <div className="recipe-modal-body">
          <form className="recipe-form" onSubmit={handleSubmit}>
            {/* Basic Information */}
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="modal-title">Recipe Title*</label>
                <input 
                  type="text"
                  id="modal-title"
                  name="title"
                  value={recipe.title}
                  onChange={handleInputChange}
                  placeholder="Enter recipe title"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-slug">Slug (URL-friendly name)</label>
                <input 
                  type="text"
                  id="modal-slug"
                  name="slug"
                  value={recipe.slug}
                  onChange={handleInputChange}
                  placeholder="Enter slug or leave blank to auto-generate"
                />
                <small className="input-hint">Leave blank to auto-generate from title</small>
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="modal-category">Category*</label>
                <select
                  id="modal-category"
                  name="category"
                  value={recipe.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="bucks_products">Buck's Products</option>
                  <option value="customer_recipes">Customer Recipes</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-product_id">Related Product</label>
                <select
                  id="modal-product_id"
                  name="product_id"
                  value={recipe.product_id}
                  onChange={handleInputChange}
                >
                  <option value="">None (Optional)</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-status">Status*</label>
                <select
                  id="modal-status"
                  name="status"
                  value={recipe.status}
                  onChange={handleInputChange}
                  required
                >
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="modal-description">Description*</label>
              <textarea
                id="modal-description"
                name="description"
                value={recipe.description}
                onChange={handleInputChange}
                placeholder="Enter recipe description"
                required
                rows="3"
              ></textarea>
            </div>
            
            {/* Recipe Details */}
            <div className="form-row">
              {recipe.category === 'customer_recipes' && (
                <>
                  <div className="form-group">
                    <label htmlFor="modal-difficulty">Difficulty*</label>
                    <select
                      id="modal-difficulty"
                      name="difficulty"
                      value={recipe.difficulty}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="Easy">Easy</option>
                      <option value="Medium">Medium</option>
                      <option value="Hard">Hard</option>
                    </select>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="modal-prep_time_min">Prep Time (minutes)*</label>
                    <input 
                      type="number"
                      id="modal-prep_time_min"
                      name="prep_time_min"
                      value={recipe.prep_time_min}
                      onChange={handleInputChange}
                      placeholder="Enter prep time in minutes"
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="modal-cook_time_min">Cook Time (minutes)*</label>
                    <input 
                      type="number"
                      id="modal-cook_time_min"
                      name="cook_time_min"
                      value={recipe.cook_time_min}
                      onChange={handleInputChange}
                      placeholder="Enter cook time in minutes"
                      min="0"
                      required
                    />
                  </div>
                </>
              )}
              
              <div className="form-group">
                <label htmlFor="modal-servings">Servings*</label>
                <input 
                  type="text"
                  id="modal-servings"
                  name="servings"
                  value={recipe.servings}
                  onChange={handleInputChange}
                  placeholder="e.g., '4', '4-6', '1 loaf'"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="modal-season">Best Season*</label>
                <select
                  id="modal-season"
                  name="season"
                  value={recipe.season}
                  onChange={handleInputChange}
                  required
                >
                  <option value="Spring">Spring</option>
                  <option value="Summer">Summer</option>
                  <option value="Fall">Fall</option>
                  <option value="Winter">Winter</option>
                  <option value="Year-round">Year-round</option>
                </select>
              </div>
            </div>
            
            {/* Image Upload */}
            <div className="form-group">
              <label htmlFor="modal-recipe-image">Recipe Image</label>
              <div className="image-upload-container">
                <input 
                  type="file"
                  id="modal-recipe-image"
                  ref={fileInputRef}
                  onChange={handleImageChange}
                  accept="image/*"
                  className="file-input"
                />
                <label htmlFor="modal-recipe-image" className="file-input-label">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
                    <circle cx="9" cy="9" r="2"></circle>
                    <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                  </svg>
                  Choose Image
                </label>
                {imageFile && (
                  <span className="file-name">
                    {imageFile.name} 
                    <button 
                      type="button" 
                      className="clear-file-btn"
                      onClick={clearImageSelection}
                    >
                      ×
                    </button>
                  </span>
                )}
                {!imageFile && recipe.image_s3_key && (
                  <span className="file-name">
                    Current image: {recipe.image_s3_key.split('/').pop()}
                  </span>
                )}
              </div>
              {(imagePreview || recipe.image_s3_key) && (
                <div className="image-preview-container">
                  <img 
                    src={imagePreview || `https://bucks-farmstand-product-images.s3.amazonaws.com/${recipe.image_s3_key}`} 
                    alt="Recipe Preview" 
                    className="image-preview" 
                  />
                </div>
              )}
            </div>
            
            {/* Ingredients */}
            <div className="form-group">
              <label htmlFor="modal-ingredient">Ingredients*</label>
              <div className="list-input-container">
                <input 
                  type="text"
                  id="modal-ingredient"
                  value={newIngredient}
                  onChange={(e) => setNewIngredient(e.target.value)}
                  placeholder="Enter an ingredient and click Add"
                  className="list-input"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddIngredient())}
                />
                <button 
                  type="button"
                  className="list-add-btn"
                  onClick={handleAddIngredient}
                >
                  Add
                </button>
              </div>
              
              <div className="items-list">
                {recipe.ingredients.length > 0 ? (
                  recipe.ingredients.map((ingredient, index) => (
                    <div className="list-item" key={index}>
                      <span>{ingredient}</span>
                      <button 
                        type="button"
                        className="remove-item-btn"
                        onClick={() => handleRemoveIngredient(index)}
                        aria-label="Remove ingredient"
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="empty-list-message">No ingredients added yet</div>
                )}
              </div>
            </div>
            
            {/* Steps */}
            <div className="form-group">
              <label htmlFor="modal-step">Steps/Instructions*</label>
              <div className="list-input-container">
                <textarea 
                  id="modal-step"
                  value={newStep}
                  onChange={(e) => setNewStep(e.target.value)}
                  placeholder="Enter a step and click Add"
                  className="list-input"
                  rows="2"
                ></textarea>
                <button 
                  type="button"
                  className="list-add-btn"
                  onClick={handleAddStep}
                >
                  Add
                </button>
              </div>
              
              <div className="items-list numbered">
                {recipe.steps.length > 0 ? (
                  recipe.steps.map((step, index) => (
                    <div className="list-item" key={index}>
                      <span className="item-number">{index + 1}.</span>
                      <span>{step}</span>
                      <button 
                        type="button"
                        className="remove-item-btn"
                        onClick={() => handleRemoveStep(index)}
                        aria-label="Remove step"
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="empty-list-message">No steps added yet</div>
                )}
              </div>
            </div>
            
            {/* Tips (Optional) */}
            <div className="form-group">
              <label htmlFor="modal-tip">Tips (Optional)</label>
              <div className="list-input-container">
                <input 
                  type="text"
                  id="modal-tip"
                  value={newTip}
                  onChange={(e) => setNewTip(e.target.value)}
                  placeholder="Enter a tip and click Add"
                  className="list-input"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTip())}
                />
                <button 
                  type="button"
                  className="list-add-btn"
                  onClick={handleAddTip}
                >
                  Add
                </button>
              </div>
              
              <div className="items-list">
                {recipe.tips.length > 0 ? (
                  recipe.tips.map((tip, index) => (
                    <div className="list-item" key={index}>
                      <span>{tip}</span>
                      <button 
                        type="button"
                        className="remove-item-btn"
                        onClick={() => handleRemoveTip(index)}
                        aria-label="Remove tip"
                      >
                        ×
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="empty-list-message">No tips added yet</div>
                )}
              </div>
            </div>
            
            {/* Form Actions */}
            <div className="form-actions modal-actions">
              <button 
                type="button"
                className="cancel-btn"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="submit-btn"
              >
                {editMode ? 'Update Recipe' : 'Save Recipe'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default RecipeFormModal;